import { toDatabaseFormat } from "@ac-expo/utils";
import { makeAutoObservable } from "mobx";
import { DateRange } from "react-day-picker";
import {
  EventTypeType,
  Dates,
  SortBy,
  PlaceTypeType,
} from "@ac-expo/api-types";
import { PlaceType } from "@prisma/client";
import { EventDataType } from "../components/Filter/FilterCardMenu/FilterCardMenu";
import { ISearchStore } from "./SearchStore";

export class FilterStore implements ISearchStore {
  place = "";
  selectedEventTypes: EventTypeType[] = [];
  selectedPlaceTypes: PlaceType[] = [];
  selectedEventTags: string[] = [];
  selectedDateOption: Dates = Dates.DEFAULT;
  rangeDate: DateRange | undefined = { from: undefined, to: undefined };
  showMap = false;
  startDate: Date | undefined = undefined;
  endDate: Date | undefined = undefined;
  activeData: EventDataType = "events";
  eventsPerPage = 28;
  currentPage = 0;
  typedSearchText = "";
  searchText = "";
  location: { lat: number; lng: number } | null = null;
  locationText = "";
  locationDistance = 20;
  debounceTimeout: NodeJS.Timeout | null = null;
  mapBoundsDebounceTimeout: NodeJS.Timeout | null = null;
  loading = true;
  zipCode: string | null = null;
  city: string | null = null;
  country: string | null = null;
  mapBounds: google.maps.LatLngBounds | null = null;
  mapZoomLevel: number | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  filterSelectedNumber() {
    const conditions: boolean[] = [
      this.selectedEventTypes.length > 0,
      this.setSelectedEventTags.length > 0,
      this.startDate !== undefined,
      this.endDate !== undefined,
      this.place.length > 0,
      this.selectedPlaceTypes.length > 0,
      this.location !== null || this.locationDistance !== null,
    ];

    return conditions.reduce((acc, condition) => {
      if (condition) {
        acc++;
      }
      return acc;
    }, 0);
  }

  setSelectedEventTypes(types: EventTypeType[]) {
    this.selectedEventTypes = types;
  }

  setSelectedPlaceTypes(types: PlaceTypeType[]) {
    this.selectedPlaceTypes = types;
  }

  setCurrentPage(page: number) {
    this.currentPage = page;
  }

  setEventsPerPage(count: number) {
    this.eventsPerPage = count;
  }

  setActiveData(_data: EventDataType) {
    //TODO: Uncomment this when we want to display touristic places
    // this.activeData = data;
    this.activeData = "events";
  }

  setSelectedEventTags(tags: string[]) {
    this.selectedEventTags = tags;
  }

  setStartDate(date: Date | undefined) {
    this.startDate = date;
  }

  setEndDate(date: Date | undefined) {
    this.endDate = date;
  }

  setPlace = (place: string) => {
    this.place = place;
  };

  setLocationText = (locationText?: string) => {
    this.locationText = locationText ?? "";
  };

  setTypedSearchText = (text: string) => {
    this.typedSearchText = text;

    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    this.debounceTimeout = setTimeout(() => {
      this.setCurrentPage(0);
      this.setSearchText(this.typedSearchText);
    }, 500);
  };

  setSearchText = (text: string) => {
    this.searchText = text;
  };

  setLocationDistance = (locationDistance: number) => {
    this.locationDistance = locationDistance;
  };

  setLocation = (location: { lat: number; lng: number } | null) => {
    this.location = location;
  };

  setSelectedDateOption(option: Dates) {
    this.selectedDateOption = option;
    if (option === null) {
      this.setRangeDate({ from: undefined, to: undefined });
    }
  }

  setRangeDate(range: DateRange | undefined) {
    this.rangeDate = range;
    this.startDate = range?.from || range?.to;
    this.endDate = range?.to || range?.from;
  }

  setShowMap(show: boolean) {
    this.showMap = show;
  }

  setDebounceTimeout(timeout: NodeJS.Timeout | null) {
    this.debounceTimeout = timeout;
  }

  isLoading() {
    return this.loading;
  }

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  setLocationFilter(
    zipCode: string | null,
    city: string | null,
    country: string | null
  ) {
    this.zipCode = zipCode;
    this.city = city;
    this.country = country;
  }

  setMapBounds(bounds: google.maps.LatLngBounds | null) {
    if (this.mapBoundsDebounceTimeout) {
      clearTimeout(this.mapBoundsDebounceTimeout);
    }

    this.mapBoundsDebounceTimeout = setTimeout(() => {
      this.setMapBoundsImmediate(bounds);
    }, 300);
  }

  setMapBoundsImmediate(bounds: google.maps.LatLngBounds | null) {
    this.mapBounds = bounds;
  }

  setMapZoomLevel(zoomLevel: number | null) {
    this.setMapZoomLevelImmediate(zoomLevel);
  }

  setMapZoomLevelImmediate(zoomLevel: number | null) {
    this.mapZoomLevel = zoomLevel;
  }

  setMapBoundsAndZoom(
    bounds: google.maps.LatLngBounds | null,
    zoomLevel: number | null
  ) {
    if (this.mapBoundsDebounceTimeout) {
      clearTimeout(this.mapBoundsDebounceTimeout);
    }

    this.mapBoundsDebounceTimeout = setTimeout(() => {
      this.setMapBoundsImmediate(bounds);
      this.setMapZoomLevelImmediate(zoomLevel);
    }, 300);
  }

  setLocationAndFilter(
    location: { lat: number; lng: number } | null,
    locationText: string,
    zipCode: string | null,
    city: string | null,
    country: string | null
  ) {
    // Batch all location-related updates to prevent double queries
    this.setLoading(true);
    this.location = location;
    this.locationText = locationText;
    this.zipCode = zipCode;
    this.city = city;
    this.country = country;
    this.setLoading(false);
  }

  resetStore() {
    this.setSelectedEventTags([]);
    this.setSelectedEventTypes([]);
    this.setSelectedDateOption(Dates.DEFAULT);
    this.setPlace("");
    this.setRangeDate({ from: undefined, to: undefined });
    this.setSelectedPlaceTypes([]);
    this.setSearchText("");
    this.setTypedSearchText("");
    this.setDebounceTimeout(null);
    if (this.mapBoundsDebounceTimeout) {
      clearTimeout(this.mapBoundsDebounceTimeout);
      this.mapBoundsDebounceTimeout = null;
    }
    this.setLocation(null);
    this.setLocationDistance(20);
    this.setLocationText("");
    this.setLocationFilter(null, null, null);
  }

  get filtersEvents() {
    return {
      sortBy: SortBy.CLOSEST_DATES,
      searchText: this.searchText,
      locationDistance: this.locationDistance,
      lat: this.location?.lat,
      long: this.location?.lng,
      dates: this.selectedDateOption,
      startDate:
        this.selectedDateOption === Dates.CUSTOM && this.startDate
          ? toDatabaseFormat(this.startDate)
          : undefined,
      endDate:
        this.selectedDateOption === Dates.CUSTOM && this.endDate
          ? toDatabaseFormat(this.endDate)
          : undefined,
      places: this.place,
      eventTypes: this.selectedEventTypes,
      placeTypes: [],
      entryFree: this.selectedEventTags.includes("free") ? true : undefined,
      entryPaid: this.selectedEventTags.includes("paid") ? true : undefined,
      venueIndoor: this.selectedEventTags.includes("indoor") ? true : undefined,
      venueOutdoor: this.selectedEventTags.includes("outdoor")
        ? true
        : undefined,
      zipCode: this.zipCode,
      city: this.city,
      country: this.country,
      bounds: this.mapBounds
        ? {
            northeast: {
              lat: this.mapBounds.getNorthEast().lat(),
              lng: this.mapBounds.getNorthEast().lng(),
            },
            southwest: {
              lat: this.mapBounds.getSouthWest().lat(),
              lng: this.mapBounds.getSouthWest().lng(),
            },
          }
        : undefined,
      zoomLevel: this.mapZoomLevel,
      pagination: {
        pageIndex: this.currentPage,
        pageSize: this.eventsPerPage,
      },
    };
  }

  get filtersPlaces() {
    return {
      name: this.place,
      searchText: this.searchText,
      locationDistance: this.locationDistance,
      lat: this.location?.lat,
      long: this.location?.lng,
      places: this.place,
      placeTypes: this.selectedPlaceTypes,
      entryFree: this.selectedEventTags.includes("free") ? true : undefined,
      entryPaid: this.selectedEventTags.includes("paid") ? true : undefined,
      venueIndoor: this.selectedEventTags.includes("indoor") ? true : undefined,
      venueOutdoor: this.selectedEventTags.includes("outdoor")
        ? true
        : undefined,
      dates: this.selectedDateOption,
      customDates:
        this.selectedDateOption === Dates.CUSTOM &&
        this.startDate &&
        this.endDate
          ? ([this.startDate, this.endDate] as [Date, Date])
          : undefined,
    };
  }
}

const filterStore = new FilterStore();
export default filterStore;
